/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'

import type { PointHover } from './ProgressPointMarker'

interface RadarPointMarkerProps {
  /** Injected by Victory: pixel position and the point's datum. */
  x?: number
  y?: number
  datum?: { index: number }
  /** Accessible name for the point, e.g. "Goals: 2". */
  labelFor: (index: number) => string
  color: string
  /** The spoke index and pixel position while hovered or focused, else null. */
  onHoverChange: (hover: PointHover | null) => void
}

const POINT_RADIUS = 4

/**
 * Victory `dataComponent` for the This Week radar's points: hover or Tab focus
 * shows the value tooltip. Nothing to select, unlike the progress points.
 */
const RadarPointMarker = ({
  x,
  y,
  datum,
  labelFor,
  color,
  onHoverChange,
}: RadarPointMarkerProps) => {
  'use memo'

  if (x === undefined || y === undefined || !datum) {
    return null
  }

  const showHover = () => onHoverChange({ index: datum.index, x, y })
  const hideHover = () => onHoverChange(null)

  return (
    <circle
      cx={x}
      cy={y}
      r={POINT_RADIUS}
      tabIndex={0}
      aria-label={labelFor(datum.index)}
      onFocus={showHover}
      onBlur={hideHover}
      onMouseEnter={showHover}
      onMouseLeave={hideHover}
      css={markerStyles(color)}
    />
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const markerStyles = (color: string) => css`
  fill: ${color};
  stroke: ${color};
  stroke-width: 1;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    stroke: ${colors.WHITE};
    stroke-width: 2;
  }
`

export default RadarPointMarker
